import { createQueryKeys } from '@lukemorales/query-key-factory';
import type { StoxlyClient } from '@stoxly/api-client';
import type { Body, Query } from '../op-types';

export function createCampaignQueries(client: StoxlyClient) {
  return createQueryKeys('campaigns', {
    // ['campaigns', 'all', { query }]
    all: (query?: Query<'campaignsFindAll'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) => client.campaignsFindAll({ queries: query, signal }),
    }),

    // ['campaigns', 'overview', { query }]
    overview: (query?: Query<'campaignsGetOverview'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) => client.campaignsGetOverview({ queries: query, signal }),
    }),

    // ['campaigns', 'chartData', { query }]
    chartData: (query?: Query<'campaignsGetChartData'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) => client.campaignsGetChartData({ queries: query, signal }),
    }),

    // ['campaigns', 'pointsSeries', { query }] — points earned/lost per bucket + the renewals ahead
    pointsSeries: (query?: Query<'campaignsGetPointsSeries'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) => client.campaignsGetPointsSeries({ queries: query, signal }),
    }),

    // ['campaigns', 'planMix', { query }]
    planMix: (query?: Query<'campaignsGetPlanMix'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) => client.campaignsGetPlanMix({ queries: query, signal }),
    }),

    // ['campaigns', 'tags'] — the distinct tags of live campaigns, for the admin filter
    tags: {
      queryKey: null,
      queryFn: ({ signal }: { signal?: AbortSignal }) => client.campaignsGetTags({ signal }),
    },

    // ['campaigns', 'detail', id, { query }]
    detail: (id: string, query?: Query<'campaignsFindById'>) => ({
      queryKey: [id, { query }],
      queryFn: ({ signal }) => client.campaignsFindById({ params: { id }, queries: query, signal }),
      contextQueries: {
        users: (usersQuery?: Query<'campaignsGetCampaignUsers'>) => ({
          queryKey: [{ query: usersQuery }],
          queryFn: ({ signal }) =>
            client.campaignsGetCampaignUsers({ params: { id }, queries: usersQuery, signal }),
        }),
      },
    }),
  });
}

export function createCampaignMutations(client: StoxlyClient) {
  return {
    create: {
      mutationFn: (body: Body<'campaignsCreate'>) => client.campaignsCreate(body),
    },
    update: {
      mutationFn: ({ id, body }: { id: string; body: Body<'campaignsUpdate'> }) =>
        client.campaignsUpdate(body, { params: { id } }),
    },
    /** Soft delete: the campaign leaves every list; its attribution ledger rows stay. */
    remove: {
      mutationFn: (id: string) => client.campaignsRemove(undefined, { params: { id } }),
    },
    /** Queue the daily campaign-points audit export for a range of days (backfill / re-run). */
    exportHistory: {
      mutationFn: (body: Body<'campaignsExportHistory'>) => client.campaignsExportHistory(body),
    },
  };
}
